import { Box, Container, Typography, useTheme, alpha } from '@mui/material'
import { motion } from 'framer-motion'

interface LegalHeroProps {
  title: string
  subtitle?: string
  lastUpdated?: string
}

const LegalHero = ({ title, subtitle, lastUpdated }: LegalHeroProps) => {
  const theme = useTheme()

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      sx={{
        position: 'relative',
        bgcolor: 'grey.900',
        color: 'white',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.15)}, transparent 70%)`,
          zIndex: 1,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: `linear-gradient(90deg, ${alpha('#fff', 0)}, ${alpha('#fff', 0.2)}, ${alpha('#fff', 0)})`,
        },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            position: 'relative',
            zIndex: 2,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                mb: 2,
                background: `linear-gradient(120deg, ${theme.palette.primary.main}, #fff)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.5)}`,
              }}
            >
              {title}
            </Typography>
          </motion.div>

          {subtitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'center',
                  color: 'grey.300',
                  maxWidth: 800,
                  mx: 'auto',
                  mb: lastUpdated ? 3 : 0,
                }}
              >
                {subtitle}
              </Typography>
            </motion.div>
          )}

          {lastUpdated && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  color: 'grey.500',
                }}
              >
                Last updated: {lastUpdated}
              </Typography>
            </motion.div>
          )}
        </Box>
      </Container>
    </Box>
  )
}

export default LegalHero 